import { LoadingOutlined } from '@ant-design/icons';
import { QueryClient } from '@tanstack/react-query';
import { Dropdown, Menu } from 'antd';
import { AxiosError } from 'axios';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { getSession, useSession } from 'next-auth/react';
import React, { ReactNode, useMemo, useState } from 'react';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { EyeOnSvg } from '../../../../assets/icons';
import AnswerSvg from '../../../../assets/icons/AnswerSvg';
import ArrowRightSvg from '../../../../assets/icons/ArrowRightSvg';
import FoldedHandsSVG from '../../../../assets/icons/FoldedHands';
import IdeasSvg from '../../../../assets/icons/IdeasSvg';
import { PollSvg } from '../../../../assets/icons/PollSvg';
import ProfileChecked from '../../../../assets/icons/ProfileChecked';
import QuickPlaySvg from '../../../../assets/icons/QuickPlaySvg';
import ShareSvg from '../../../../assets/icons/ShareSvg';
import emptyTemplateCoverPhoto from '../../../../assets/images/emptyTemplateCoverPhoto.png';
import theme from '../../../../assets/theme';
import { Button } from '../../../../components/_atoms/Button';
import ConditionalWrapper from '../../../../components/_atoms/ConditionalWrapper';
import DropDownButton, {
  DropdownButtonItem,
} from '../../../../components/_atoms/DropdownButton/DropdownButton';
import { IconWrapper } from '../../../../components/_atoms/IconWrapper';
import ResumeLessonModal from '../../../../components/_atoms/ResumeLessonModal';
import Component404 from '../../../../components/_molecules/Component404';
import CreatorShareComponent from '../../../../components/_molecules/CreatorShareComponent';
import GameImageRenderer from '../../../../components/_molecules/GameImageRenderer';
import LiveEmptyState from '../../../../components/_molecules/LiveEmptyState';
import { WorkspaceUser } from '../../../../components/_molecules/WorkspaceUser';
import { FullScreenSlidePreview } from '../../../../components/FullScreenSlidePreview';
import NewAppLayout from '../../../../components/layouts/NewAppLayout';
import { Slide } from '../../../../components/SlideComponents/slide.types';
import { findRelatedSlidesWithoutIndex } from '../../../../components/SlideComponents/slideUtil';
import { CSSSlideWrapperViewer } from '../../../../components/SlideComponents/SlideWrapperViewer';
import useAccessBadge from '../../../../hooks/useAccessBadge';
import useAuthGuardPrompt from '../../../../hooks/useAuthGuardPrompt';
import { useBreakPoint } from '../../../../hooks/useBreakPoint';
import { useCroppedImage } from '../../../../hooks/useCroppedImage';
import useDuplicateTemplate from '../../../../hooks/useDuplicateTemplate';
import useEndAndStartNewLesson from '../../../../hooks/useEndAndStartNewLesson';
import useFactCheckPopup from '../../../../hooks/useFactcheckPopup';
import useGames from '../../../../hooks/useGames';
import { LessonMediaPreFetcher } from '../../../../hooks/useMediaPreFetcher';
import { useMetrics } from '../../../../hooks/useMetrics';
import usePresent from '../../../../hooks/usePresent';
import usePreviewGame from '../../../../hooks/usePreviewGame';
import usePublicTemplate from '../../../../hooks/usePublicTemplate';
import useRoutePrefetch from '../../../../hooks/useRoutePrefetch';
import useRouterMatch from '../../../../hooks/useRouterMatch';
import useTemplate from '../../../../hooks/useTemplate';
import useTenants from '../../../../hooks/useTenants';
import useTenantUsers from '../../../../hooks/useTenantUsers';
import LiveActionsDisabledProvider from '../../../../providers/LiveActionsDisabledProvider';
import { usePaywall } from '../../../../providers/PaywallProvider/PaywallProvider';
import { LessonAttachment } from '../../../../services/backendService/backendService';
import { PreviewTemplate } from '../../../../services/backendService/types';
import CoreAPIService from '../../../../services/CoreAPIService';
import TemplateService from '../../../../services/TemplateService';
import { Template } from '../../../../services/TemplateService/Template.type';
import { GlobalStyles } from '../../../../styles/GlobalStyles';
import { getBaseUrl } from '../../../../utils/client';
import { typeToActivityMap } from '../../../../utils/creator';
import { detectAndBestEffortSetLanguage } from '../../../../utils/localisationUtils';
import { dehydrateReactQueryPrefetchedInfiniteQuery } from '../../../../utils/react-query-utils';
import { getTitle } from '../../../../utils/templateUtils';

const LessonPreviewPage = () => {
  const router = useRouter();
  const templateId = router.query.templateId as string;
  const session = useSession();
  return (
    <NewAppLayout allowUnAuthenticated={true}>
      <PageContainer $isAuthenticated={session.status === 'authenticated'}>
        <LessonDataFetcher templateId={templateId}>
          <TemplatePreview isAuthenticated={session.status === 'authenticated'} />
        </LessonDataFetcher>
      </PageContainer>
    </NewAppLayout>
  );
};

const LessonDataFetcher = ({
  templateId,
  children,
}: {
  templateId: string;
  children: React.ReactNode;
}) => {
  return (
    <PublicLessonDataFetcher templateId={templateId}>
      <>{children}</>
    </PublicLessonDataFetcher>
  );
};

const LessonHead = ({
  template,
  isAuthenticated,
  isOwner,
}: {
  template: Omit<PreviewTemplate, 'creatorProfileId'>;
  isAuthenticated: boolean;
  isOwner: boolean;
}) => {
  const router = useRouter();
  const { imageUrl: coverImageUrl, isSuccess } = useCroppedImage({
    fileId: template.coverImageId,
    isDiscoverTemplate: false,
    isAuthenticated: isAuthenticated,
    fileMimetype: 'image/jpeg',
    isOwner,
  });
  if (!template) return null;
  return (
    <Head>
      <title>{template.title || 'Lesson Preview'} - Curipod</title>
      <meta
        property="og:title"
        content={`${template.title || 'Lesson Preview'} - Curipod`}
        key="title"
      />
      <meta
        name="twitter:title"
        content={`${template.title || 'Lesson Preview'} - Curipod`}
      />
      <meta
        name="description"
        content={
          template.description ||
          'Free ready to play interactive lessons that sparks your students curiosity in the classroom'
        }
        key="namedescription"
      />
      <meta
        property="description"
        content={
          template.description ||
          'Free ready to play interactive lessons that sparks your students curiosity in the classroom'
        }
        key="description"
      />
      <meta
        property="og:description"
        content={
          template.description ||
          'Free ready to play interactive lessons that sparks your students curiosity in the classroom'
        }
        key="og:description"
      />
      <meta
        name="twitter:description"
        content={
          template.description ||
          'Free ready to play interactive lessons that sparks your students curiosity in the classroom'
        }
      />
      <meta
        property="og:image"
        content={isSuccess ? coverImageUrl : emptyTemplateCoverPhoto.src}
        key="imageurl"
      />
      <meta
        property="og:image:secure_url"
        content={isSuccess ? coverImageUrl : emptyTemplateCoverPhoto.src}
        key="imagesecureurl"
      />
      <meta property="og:type" content="website" />

      <meta property="og:url" content={`${getBaseUrl()}${router.asPath}`} key="ogurl" />
      <meta property="og:image:type" content={'image/jpeg'} key="imagetype" />
      <meta
        property="og:image:alt"
        content={
          template.description ||
          'Free ready to play interactive lessons that sparks your students curiosity in the classroom'
        }
        key="imageAlt"
      />
      <meta property="og:image:width" content={'300'} key="imageWidth" />
      <meta property="og:image:height" content={'190'} key="imageHeight" />
      <meta
        name="twitter:image"
        content={isSuccess ? coverImageUrl : emptyTemplateCoverPhoto.src}
      />
      <meta name="twitter:site" content={'@curipodofficial'} />
      <meta name="twitter:card" content={'summary'} />
      <meta name="twitter:widgets:theme" content="light" />
      <meta name="twitter:url" content={`${getBaseUrl()}${router.asPath}`} />
    </Head>
  );
};

const PublicLessonDataFetcher = ({
  templateId,
  children,
}: {
  templateId: string;
  children: React.ReactNode;
}) => {
  const { tenantId, urlTenantId } = useRouterMatch();
  const { data: userTenantData } = useTenants();
  const { template, error } = usePublicTemplate(
    templateId,
    urlTenantId,
    userTenantData?.user.activeTenantId,
  );
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const previewTemplate = useMemo(() => {
    if (!template) return undefined;
    const previewTemplate: Omit<PreviewTemplate, 'creatorProfileId'> = {
      id: template.id,
      creatorId: template.creator.uid,
      slides: template.content.slides || [],
      coverImageId: template.coverImageId,
      coverImageAltText: template.coverImageAltText,
      title: template.title,
      creatorName:
        userAccessBadge?.user && template.creator.uid === userAccessBadge?.user.id
          ? 'me'
          : template.creator.uid,
      modifiedAt: template.modifiedAt,
      description: template.description,
      tenantId: tenantId as string,
      activeGame: template.content.activeGame,
      isPrivate: template.isPrivate,
      language: template.language || null,
      metadata: template.metadata,
      aiContent: template.aiContent,
      downloadCount: 0,
      playCount: 0,
      attachments: template.attachments,
    };
    return previewTemplate;
  }, [userAccessBadge?.user, template, tenantId]);

  return (
    <TemplateContext.Provider value={{ template: previewTemplate, error }}>
      {children}
    </TemplateContext.Provider>
  );
};

const TemplateContext = React.createContext<{
  template: Omit<PreviewTemplate, 'creatorProfileId'> | undefined;
  error: AxiosError | null;
}>({ template: undefined, error: null });

const TemplatePreview = ({ isAuthenticated }: { isAuthenticated: boolean }) => {
  const { t } = useTranslation('Host');
  const { template, error } = useContext(TemplateContext);
  const { tenantId } = useRouterMatch();

  useRoutePrefetch(`/[tenantId]/lessons/[templateId]/edit`, undefined, {
    priority: true,
  });
  useRoutePrefetch(`/[tenantId]/games/[gameId]/live`, undefined, {
    priority: false,
  });
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const isOwner =
    !!userAccessBadge?.user && userAccessBadge?.user.id == template?.creatorId;
  useFactCheckPopup({
    templateId: template?.id,
    isAiGenerated: !!template?.metadata?.['aiTopic'],
    isOwner: isOwner,
  });
  const [showSlideModal, setShowSlideModal] = useState(false);
  const [selectedSlide, setSelectedSlide] = useState<Slide>();
  useEffect(() => {
    if (template) {
      if (template.slides) {
        setSelectedSlide(template.slides[0]);
      }
    }
  }, [template]);

  const slidesToView = useMemo(() => {
    const slidesToCheck = template?.slides || [];
    return slidesToCheck;
  }, [template?.slides]);

  const slidesToRender = useMemo(() => {
    return slidesToView.filter(
      r => r.type !== 'voting' && r.type !== 'podium' && r.type !== 'responsegrid',
    );
  }, [slidesToView]);

  const relatedSelectedSlides = useMemo(() => {
    if (!selectedSlide) return;
    return findRelatedSlidesWithoutIndex(selectedSlide.id, template?.slides || []);
  }, [selectedSlide, template?.slides]);

  if (error?.response?.status === 404) return <Component404 />;
  if (error?.response?.status === 403)
    return (
      <NoAccessContainer>
        <LiveEmptyState
          title={t('NoLessonAccess', 'No access to lesson')}
          icon={<FoldedHandsSVG />}
          description={t(
            'NoAccessToLesson',

            `You don't have access to this lesson. Ask the owner to grant you access, or return to one of your own lessons.`,
          )}
        />
      </NoAccessContainer>
    );

  return (
    <MainContainer>
      <GlobalStyles />
      {showSlideModal && relatedSelectedSlides && selectedSlide && template && (
        <FullScreenSlidePreview onClose={() => setShowSlideModal(false)}>
          <LiveActionsDisabledProvider>
            <CSSSlideWrapperViewer
              aspect={{ height: 1080, width: 1920 }}
              slide={selectedSlide}
              backgroundMedia={relatedSelectedSlides[0].backgroundMedia}
              slides={template.slides}
              resultView={false}
              showStaticPoll={false}
              gameId={template.activeGame?.id}
              tenantId={tenantId}
              hideLogo={false}
              isDiscoverTemplate={false}
              isOwner={true}
              isAuthenticated={true}
              isUsingSockets={false}
              uniqueId={`slide-result-view-${selectedSlide.id}`}
            ></CSSSlideWrapperViewer>
          </LiveActionsDisabledProvider>
        </FullScreenSlidePreview>
      )}
      <LessonMediaPreFetcher
        slides={template?.slides}
        isAuthenticated={isAuthenticated}
        isDiscoveredTemplate={false}
        isOwner={isOwner}
      />
      {template && (
        <LessonHead
          template={template}
          isAuthenticated={isAuthenticated}
          isOwner={isOwner}
        />
      )}
      {template && (
        <TopSection>
          <ImageContainer>
            {template.coverImageId && (
              <GameImageRenderer
                fileId={template.coverImageId}
                fileMimetype={'image/jpeg'}
                fileAltText={template.coverImageAltText}
                isDiscoverTemplate={false}
                isAuthenticated={isAuthenticated}
                objectFit="fill"
                isOwner={isOwner}
              />
            )}
            {!template.coverImageId && (
              <img src={emptyTemplateCoverPhoto.src} alt={t('Curipod placeholder')} />
            )}
          </ImageContainer>

          <LessonInfoSection
            template={template}
            isOwner={isOwner}
            isAuthenticated={isAuthenticated}
          />
        </TopSection>
      )}
      <div style={{ width: '100%' }}>
        <h2>Scroll to see all the slides</h2>
        {template && (
          <VerticalSlideList
            slides={slidesToRender}
            gameId={template.activeGame?.id}
            tenantId={tenantId}
            lessonAttachments={template.attachments}
          />
        )}
      </div>
    </MainContainer>
  );
};

const ImageContainer = styled.div`
  width: 100%;
  height: 100%;
  max-height: 200px;
  overflow: hidden;
  object-fit: contain;
  display: flex;
  align-items: center;
  border-radius: 10px;
  margin: 0 auto;

  img {
    width: 100%;
    height: auto;
  }

  @media (max-width: 950px) {
    max-width: 300px;
  }
`;

const TopSection = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 20px;
  margin-top: 20px;
  margin-bottom: 20px;
  @media (max-width: 950px) {
    grid-template-columns: 1fr;
  }
`;

const LessonInfoSection = ({
  template: previewTemplate,
  isOwner,
  isAuthenticated,
}: {
  template: Omit<PreviewTemplate, 'creatorProfileId'>;
  isOwner: boolean;
  isAuthenticated: boolean;
}) => {
  const { router, tenantId } = useRouterMatch();
  const { users } = useTenantUsers();
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const metrics = useMetrics();
  const { promptPaywall, shouldPromptPaywall } = usePaywall();
  const { duplicateTemplate, isDuplicatingTemplate } = useDuplicateTemplate();
  const [isCopyAndEdit, setIsCopyAndEdit] = useState(false);
  const { isLoadingPreview, createPreviewGame } = usePreviewGame(
    !previewTemplate.isPrivate,
  );
  const isDesktop = useBreakPoint(600);
  const isLargerThan500 = useBreakPoint(500);
  const { presentLesson, isCreatingGame } = usePresent();

  const { games, canFetchMore } = useGames(previewTemplate.id);

  const numberOfReports = useMemo(() => {
    return games?.length === 10 && canFetchMore ? '10+' : games?.length;
  }, [canFetchMore, games?.length]);

  const { template } = useTemplate(previewTemplate.id);

  const { authGuardComponent, showAuthGuard } = useAuthGuardPrompt(
    previewTemplate.id,
    undefined,
  );

  const { handleEndAndStartNewLesson } = useEndAndStartNewLesson();

  const [isOnGoingGameModalVisible, setIsOnGoingGameModalVisible] = useState(false);

  // Check if template contains AI activities
  const hasAiActivities = useMemo(() => {
    return previewTemplate.slides.some(
      slide =>
        slide.type === 'aiAssessedAnswers' || slide.type === 'aiAssessedImageAnswers',
    );
  }, [previewTemplate.slides]);

  const isWorkspaceUser = useMemo(() => {
    if (users && userAccessBadge?.user) {
      return users.find(u => u.id === userAccessBadge.user.id);
    }
  }, [users, userAccessBadge?.user]);

  const handlePresent = () => {
    // Check if template has AI activities and show paywall if needed
    if (hasAiActivities && shouldPromptPaywall()) {
      promptPaywall(
        'LessonPage',
        "You're unable to present this lesson because it contains AI activities and Curipod AI is at capacity.",
      );
      return;
    }

    if (template && template.content.slides && template.content.slides?.length > 0) {
      metrics.logEvent('LessonPage.Present', {
        templateId: template.id,
        withModeration: false,
        fromBeginning: template.content.activeGame?.isOngoing ? false : true,
      });
      if (isOwner) {
        presentLesson({
          template: template,
          method: 'present',
          fromBeginning: template.content.activeGame?.isOngoing ? false : true,
          gameId: template.content.activeGame?.id,
        });
      } else {
        if (!isDuplicatingTemplate && tenantId) {
          duplicateTemplate({
            templateId: template.id,
            isPrivate: true,
            onSuccessFunction: (template: Template) => {
              presentLesson({
                template: { ...template, metadata: template.metadata },
                method: 'present',
                fromBeginning: true,
                gameId: template?.content.activeGame?.id,
              });
            },
          });
        }
      }
    }
  };

  const shareMenu = (
    <Menu style={{ maxWidth: 340 }}>
      <CreatorShareComponent
        isGeneratedByAI={!!previewTemplate.metadata?.['aiTopic']}
        templateId={previewTemplate.id}
        templateTitle={previewTemplate?.title || 'Curipod lesson'}
        isGenerator={!!previewTemplate?.aiContent?.prompts?.length}
      />
    </Menu>
  );

  const handleEdit = () => {
    metrics.logEvent('LessonPage.Lesson.AddToMyLesson', {
      isPrivate: previewTemplate.isPrivate,
      isOwner,
    });

    if (!isDuplicatingTemplate && tenantId) {
      setIsCopyAndEdit(true);
      duplicateTemplate({
        templateId: previewTemplate.id,
        isPrivate: true,
        onSuccessFunction: (template: Template) => {
          setIsCopyAndEdit(false);
          router.push(`/${tenantId}/lessons/${template.id}/edit`);
        },
      });
    }
  };

  const handleStartNewLesson = () => {
    // End the current lesson
    // Start a new lesson
    if (template?.content.activeGame?.id) {
      // Can only start new lesson from here with an already existing game.
      // Otherwise this does not make sense at all.
      handleEndAndStartNewLesson({
        gameId: template?.content.activeGame?.id,
        template: template,
      });
    }
  };

  const handleEndGame = () => {
    //not used on this page
  };

  const lessonPreviewActionMenu = (
    <Menu>
      <Menu.Item
        key="openpreview"
        onClick={() => {
          if (!isAuthenticated) {
            showAuthGuard();
          } else {
            // Check if template has AI activities and show paywall if needed
            if (hasAiActivities && shouldPromptPaywall()) {
              promptPaywall(
                'LessonPage',
                "You're unable to preview this lesson because it contains AI activities and Curipod AI is at capacity.",
              );
              return;
            }

            isDesktop &&
              !isLoadingPreview &&
              createPreviewGame({
                templateId: previewTemplate.id,
                onSuccessFunction: data => {
                  metrics.logEvent('LessonPage.Open.PreviewLesson', {
                    templateId: previewTemplate.id,
                    isFromDiscover: previewTemplate.isPrivate,
                  });
                  router.push(
                    `/${tenantId}/lessons/${data.templateId}/preview/${data.id}`,
                  );
                },
              });
            if (!isDesktop) {
              handlePresent();
            }
          }
        }}
      >
        <DropdownButtonItem
          isDarkTheme={false}
          icon={
            <IconWrapper>
              {isLoadingPreview || isCreatingGame ? <LoadingOutlined /> : <EyeOnSvg />}
            </IconWrapper>
          }
          title={isDesktop ? 'Preview lesson' : 'Start lesson'}
          description={
            isDesktop
              ? 'Preview the lesson in your own pace with a teacher and student view'
              : 'Start this lesson and teach it with your students'
          }
        />
      </Menu.Item>
    </Menu>
  );

  return (
    <>
      {authGuardComponent}
      <div style={{ width: '100%' }}>
        <LessonSavedContainer>
          {!isDesktop && (
            <NoteContainer
              $color={theme.colors.orange}
              $backgroundColor={'#FAE3CD'}
              style={{ gridColumn: '1/5' }}
            >
              <IconWrapper
                cursor="default"
                width={isLargerThan500 ? '25px' : '20px'}
                height={isLargerThan500 ? '25px' : '20px'}
                style={isLargerThan500 ? { margin: '10px' } : { margin: '5px' }}
              >
                <IdeasSvg />
              </IconWrapper>
              <CuripodWorksBestSpan>
                Switch to desktop or tablet for best experience
              </CuripodWorksBestSpan>
            </NoteContainer>
          )}
        </LessonSavedContainer>
        <Title
          style={{
            fontSize: isDesktop ? 30 : 24,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {previewTemplate.title ||
            getTitle({
              title: previewTemplate?.title,
              slides: previewTemplate?.slides,
              maxLength: 40,
            })}
          <span>
            {!!isWorkspaceUser && previewTemplate.isPrivate === false && (
              <ConditionalWrapper
                condition={isOwner}
                truthyWrapper={(children: ReactNode) => (
                  <Link href={`/${tenantId}/profile`}>
                    <a>{children}</a>
                  </Link>
                )}
              >
                <ProfileSection
                  type="ghost"
                  onClick={() => {
                    metrics.logEvent('LessonPage.Lesson.Creator', undefined);
                  }}
                >
                  <WorkspaceUser uid={previewTemplate.creatorId} showName={true} />
                  <div>
                    <h2>
                      <span>
                        <IconWrapper height="16px" width="16px" color={'#ffa55b'}>
                          <ProfileChecked />
                        </IconWrapper>
                      </span>
                    </h2>
                  </div>
                </ProfileSection>
              </ConditionalWrapper>
            )}
          </span>
        </Title>
        <ButtonContainer>
          <>
            {(isOwner || !isAuthenticated) && (
              <>
                {previewTemplate.activeGame?.isOngoing && (
                  <ResumeLessonModal
                    isOnGoingGameModalVisible={!!isOnGoingGameModalVisible}
                    onStartNewLesson={handleStartNewLesson}
                    onResumeLesson={handlePresent}
                    currentPage="lessonPage"
                    onEndLesson={handleEndGame}
                    onCancel={() => setIsOnGoingGameModalVisible(false)}
                  />
                )}
                <DropDownButton
                  menu={lessonPreviewActionMenu}
                  isLoading={isCreatingGame}
                  icon={
                    <IconWrapper height="20px" width="20px">
                      {isDesktop ? <QuickPlaySvg /> : <EyeOnSvg />}
                    </IconWrapper>
                  }
                  onClick={() => {
                    if (!isAuthenticated) {
                      showAuthGuard();
                    } else {
                      // Check if template has AI activities and show paywall if needed
                      if (hasAiActivities && shouldPromptPaywall()) {
                        promptPaywall(
                          'LessonPage',
                          "You're unable to present this lesson because it contains AI activities and Curipod AI is at capacity.",
                        );
                        return;
                      }

                      metrics.logEvent('LessonPage.Present', {
                        templateId: previewTemplate.id,
                        isFromDiscover: !previewTemplate.isPrivate,
                      });

                      if (previewTemplate.activeGame?.isOngoing) {
                        setIsOnGoingGameModalVisible(true);
                        return;
                      }
                      handlePresent();
                    }
                  }}
                  isDisabled={previewTemplate.slides.length === 0}
                >
                  {isDesktop ? 'Start lesson' : 'Preview lesson'}
                </DropDownButton>
              </>
            )}
            {!isOwner && isAuthenticated && (
              <Button
                icon={<QuickPlaySvg />}
                onClick={() => {
                  // Check if template has AI activities and show paywall if needed
                  if (hasAiActivities && shouldPromptPaywall()) {
                    promptPaywall(
                      'LessonPage',
                      "You're unable to present this lesson because it contains AI activities and Curipod AI is at capacity.",
                    );
                    return;
                  }

                  metrics.logEvent('LessonPage.Present', {
                    templateId: previewTemplate.id,
                    isFromDiscover: !previewTemplate.isPrivate,
                  });
                  handlePresent();
                }}
                disabled={previewTemplate.slides.length === 0}
                styles={{
                  minWidth: '170px',
                  display: 'flex',
                  width: 'fit-content',
                  gap: '5px',
                }}
                loading={isCreatingGame}
              >
                {isDesktop ? 'Start lesson' : 'Preview lesson'}
              </Button>
            )}
            {isOwner && (
              <Link href={`/${tenantId}/lessons/${previewTemplate.id}/edit`} passHref>
                <a>
                  <StyledButton
                    height="48px"
                    type="primary"
                    onClick={() => {
                      metrics.logEvent('LessonPage.Lesson.Edit', {
                        isPrivate: previewTemplate.isPrivate,
                        isOwner,
                      });
                    }}
                  >
                    <IconWrapper height="20px" width="20px">
                      <AnswerSvg />
                    </IconWrapper>
                    Edit
                  </StyledButton>
                </a>
              </Link>
            )}
            {!isOwner && isAuthenticated && (
              <StyledButton
                height="48px"
                type="primary"
                icon={
                  <IconWrapper height="20px" width="20px">
                    <AnswerSvg />
                  </IconWrapper>
                }
                onClick={() => {
                  handleEdit();
                }}
              >
                {isDuplicatingTemplate && isCopyAndEdit ? (
                  <LoadingOutlined />
                ) : (
                  'Copy & Edit'
                )}
              </StyledButton>
            )}
            {!isOwner && !isAuthenticated && (
              <StyledButton
                icon={
                  <IconWrapper height="20px" width="20px">
                    <AnswerSvg />
                  </IconWrapper>
                }
                height="48px"
                type="primary"
                onClick={showAuthGuard}
              >
                Copy & Edit
              </StyledButton>
            )}
            <Dropdown placement="bottomRight" trigger={['click']} overlay={shareMenu}>
              <ShareLessonsButton>
                <IconWrapper style={{ marginRight: 5 }} height="20px" width="20px">
                  <ShareSvg />
                </IconWrapper>
                <span>Share lesson</span>
                <IconWrapper
                  style={{ rotate: '90deg', marginLeft: 0 }}
                  height="20px"
                  width="20px"
                >
                  <ArrowRightSvg />
                </IconWrapper>
              </ShareLessonsButton>
            </Dropdown>
          </>

          {isOwner && template && (
            <>
              {games && games.length > 0 && (
                <Link href={`/${tenantId}/reports?templateId=${template.id}`} passHref>
                  <a>
                    <StyledButton
                      styles={{ position: 'relative' }}
                      $color={'transparent'}
                      height="48px"
                      type="linkStyle"
                      onClick={() => {
                        metrics.logEvent('LessonPage.Lesson.SeeReports', {
                          isPrivate: previewTemplate.isPrivate,
                          isOwner,
                        });
                      }}
                    >
                      <NumberOfReports>
                        <span>{numberOfReports}</span>
                      </NumberOfReports>
                      <IconWrapper height="20px" width="20px">
                        <PollSvg />
                      </IconWrapper>
                      See all reports
                    </StyledButton>
                  </a>
                </Link>
              )}
            </>
          )}
        </ButtonContainer>
      </div>
      <div style={{ gridColumn: '1 / -1' }}>
        {previewTemplate.description && (
          <>
            <h3 style={{ fontSize: 26 }}>Description</h3>
            <p
              style={{
                width: '100%',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                fontSize: '16px',
              }}
            >
              {previewTemplate.description}
            </p>
          </>
        )}
      </div>
    </>
  );
};

const NumberOfReports = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 5px;
  left: 15px;
  background: ${theme.colors.black};
  color: ${theme.colors.white};
  border-radius: 50%;
  height: 15px;
  width: 15px;
  span {
    font-size: 8px;
  }
`;

const StyledButton = styled(Button)<{ $color?: string }>`
  ${({ $color = theme.colors.blue }) => css`
    background-color: ${$color};
    font-size: 16px;
    min-width: 170px;

    &:hover,
    &:focus,
    &:active {
      background-color: ${$color};
    }
  `}
`;

const ShareLessonsButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  padding: 0px 10px;
  box-shadow: none;

  background: transparent;
  color: ${theme.colors.black};

  &:hover,
  &:focus,
  &:active {
    background: transparent;
    color: ${theme.colors.iconGrey};
  }

  svg {
    margin: 0;
  }
`;

const VerticalSlideList = ({
  slides,
  gameId,
  tenantId,
  lessonAttachments,
}: {
  slides: Slide[];
  gameId: string | undefined;
  tenantId: string;
  lessonAttachments?: LessonAttachment[];
}) => {
  const { template } = useContext(TemplateContext);

  const session = useSession();
  const isOwner = session && session.data?.user?.uid == template?.creatorId;

  const isAuthenticated = session.status === 'authenticated';
  return (
    <VerticalSlideListContainer>
      {slides.map((slide, i) => {
        return (
          <VerticalSlideContainer key={i}>
            <div
              style={{
                borderRadius: 10,
                overflow: 'hidden',
                boxShadow: theme.effects.defaultShadow,
              }}
            >
              <LiveActionsDisabledProvider key={`slide${i}`}>
                <CSSSlideWrapperViewer
                  aspect={{ height: 1080, width: 1920 }}
                  slide={slide}
                  backgroundMedia={
                    findRelatedSlidesWithoutIndex(slide.id, slides)[0]?.backgroundMedia
                  }
                  slides={slides}
                  resultView={false}
                  showStaticPoll={true}
                  gameId={gameId}
                  tenantId={tenantId}
                  hideLogo={false}
                  isDiscoverTemplate={false}
                  isOwner={isOwner}
                  isAuthenticated={isAuthenticated}
                  preventInteraction={false}
                  isUsingSockets={false}
                  uniqueId={`slide-list-${slide.id}`}
                  lessonAttachments={lessonAttachments}
                ></CSSSlideWrapperViewer>
              </LiveActionsDisabledProvider>
            </div>
            <div>
              <p style={{ marginBottom: 0, fontSize: 18 }}>
                <strong>Slide:</strong> {i + 1} / {slides.length}
              </p>
              <p style={{ marginBottom: 0, fontSize: 18 }}>
                <strong>Activity:</strong>{' '}
                {typeToActivityMap(slide.type)?.title === 'Slide'
                  ? 'Content slide'
                  : typeToActivityMap(slide.type)?.title}
              </p>
            </div>
          </VerticalSlideContainer>
        );
      })}
    </VerticalSlideListContainer>
  );
};

const VerticalSlideContainer = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr;
  gap: 20px;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 5px;
  }
`;
const VerticalSlideListContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  max-width: 1000px;
  margin: 0 auto 20px auto;

  @media (max-width: 800px) {
    margin-bottom: 80px;
  }
`;

const Title = styled.h1`
  color: ${theme.colors.black};
  font-weight: bold;
  font-size: 24px;
`;

const ProfileSection = styled(Button)`
  display: flex;
  align-items: left;
  justify-content: flex-start;
  margin: 20px 0px;
  padding-left: 5px;
  padding-right: 5px;
  box-shadow: none;
  border: none;

  h2 {
    display: flex;
    align-items: center;
  }

  span {
    display: flex;
    align-items: baseline;
    padding-right: 6px;
  }

  h2,
  p {
    margin: 0px 0px 0px 20px;
    font-size: 14px;
  }

  p {
    color: ${theme.colors.text.dark};
  }

  @media (max-width: 900px) {
    p {
      font-size: 11px;
    }
  }
`;
const CuripodWorksBestSpan = styled.span`
  font-size: 15px;
  font-weight: bold;
  @media (min-width: 500px) {
    font-size: 20px;
  }
`;

const LessonSavedContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 6fr 1fr 1fr;
`;

const ButtonContainer = styled.div`
  display: flex;
  max-width: 650px;
  flex-wrap: wrap;
  flex: 1 1;
  gap: 10px;

  a {
    margin: ;
  }
`;

const PageContainer = styled.div<{ $isAuthenticated: boolean }>`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  @media (min-width: 800px) {
    padding-left: ${({ $isAuthenticated }) => ($isAuthenticated ? '240px' : '0px')};
  }
`;
const NoteContainer = styled.div<{ $color: string; $backgroundColor: string }>`
  display: flex;
  justify-content: flex-start;
  padding: 5px;
  min-height: 40px;
  font-size: 10px;
  margin: 5px;
  align-items: center;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  color: ${({ $color }) => $color};
  opacity: 0.75;
  border-radius: 10px;
`;
const MainContainer = styled.div`
  width: 100%;
  max-width: 1000px;
  display: flex;
  flex-direction: column;
  height: fit-content;
  padding-top: 60px;
  margin: 0px 20px;
  position: relative;

  @media (min-width: 1300px) {
    margin: 0 auto;
  }
`;

const NoAccessContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 20px;
  align-items: center;
`;

export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=10, stale-while-revalidate=86400',
  );
  const queryClient = new QueryClient();
  const session = await getSession({ req: context.req });
  const { templateId } = context.query as {
    templateId: string;
  };

  const template = await CoreAPIService.getTemplate(templateId);
  if (template) {
    const isBlockedFromSharing = template.isBlockedFromSharing;

    if (!isBlockedFromSharing && session) {
      await queryClient.prefetchQuery(
        ['template', templateId],
        () => {
          return TemplateService.templateDataToClientTemplate(template, session.user.uid);
        },
        { staleTime: 1000 * 60, retry: false },
      );
    }
    if (isBlockedFromSharing && session) {
      if (session.user.uid === template.creator.uid) {
        await queryClient.prefetchQuery(
          ['template', templateId],
          () => {
            return TemplateService.templateDataToClientTemplate(
              template,
              session.user.uid,
            );
          },
          { staleTime: 1000 * 60, retry: false },
        );
      } else {
        // Do nothing
      }
    } else {
      // Do nothing
    }
  } else {
    // Do nothing
  }

  return {
    props: {
      dehydratedState: dehydrateReactQueryPrefetchedInfiniteQuery(queryClient),
      ...(await detectAndBestEffortSetLanguage(context)),
      session,
    },
  };
};

export default LessonPreviewPage;
